import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * AdminUpdateRequests — Ponte richieste aggiornamento TM/PESDB (soluzione C).
 *
 * I presidenti chiedono l'aggiornamento dei propri giocatori (pulsanti "Richiedi agg. TM/PESDB"):
 * in produzione lo scraping è bloccato (IP), quindi nasce solo una RICHIESTA pending.
 * Flusso admin:
 *   ① In PRODUZIONE: guarda l'elenco e premi "Esporta richieste" → scarichi un file.
 *   ② In PREVIEW: carichi quel file e premi "Elabora (scraping)" → scarichi il file dei risultati.
 *   ③ In PRODUZIONE: carichi il file dei risultati e premi "Applica" → i dati vengono aggiornati
 *      e le richieste segnate come completate.
 */
export default function AdminUpdateRequests() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState("");
    const [processResult, setProcessResult] = useState(null);
    const [processProgress, setProcessProgress] = useState(null);
    const [applyResult, setApplyResult] = useState(null);
    const [scanResult, setScanResult] = useState(null);
    const processInputRef = useRef(null);
    const applyInputRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/update-requests");
            setData(data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    };

    useEffect(() => { load(); }, []);

    const deleteRequest = async (r) => {
        if (!window.confirm(`Eliminare la richiesta ${r.kind === "tm" ? "TM" : "PESDB"} di ${r.player_name || "questo giocatore"}?\n(Usa questo se l'hai già sistemata a mano.)`)) return;
        setErr(""); setBusy(`del-${r.id}`);
        try {
            await api.delete(`/admin/update-requests/${r.id}`);
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy("");
        }
    };

    const downloadJson = (obj, filename) => {
        const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ① PRODUZIONE: esporta le richieste pending
    const exportRequests = async () => {
        setErr(""); setBusy("export");
        try {
            const { data } = await api.get("/admin/update-requests/export");
            downloadJson(data, "richieste-aggiornamento.json");
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(""); }
    };

    // ② PREVIEW: carica il file richieste → scraping in BACKGROUND (con polling) → scarica risultati
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const processFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setErr(""); setProcessResult(null); setProcessProgress(null); setBusy("process");
        try {
            const parsed = JSON.parse(await file.text());
            const payload = { tm: parsed.tm || [], pesdb: parsed.pesdb || [] };
            const { data: start } = await api.post("/admin/update-requests/process", payload);
            const jobId = start.job_id;
            setProcessProgress({ done: 0, total: start.total || 0, phase: "tm" });
            let job = null;
            let attempts = 0;
            for (;;) {
                await sleep(3000);
                if (++attempts > 400) throw new Error("Timeout: elaborazione troppo lunga, riprova.");
                const { data } = await api.get(`/admin/update-requests/process-status/${jobId}`);
                setProcessProgress({ done: data.done || 0, total: data.total || 0, phase: data.phase });
                if (data.status === "done") { job = data; break; }
                if (data.status === "error") { throw new Error(data.error || "Errore durante l'elaborazione"); }
            }
            const result = job.result;
            setProcessResult(result);
            downloadJson(result, "risultati-aggiornamento.json");
        } catch (e) {
            setErr(e.response?.data?.detail || e.message || "File non valido");
        } finally { setBusy(""); setProcessProgress(null); }
    };

    // ③ PRODUZIONE: carica il file risultati → applica + segna done
    const applyFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setErr(""); setApplyResult(null); setBusy("apply");
        try {
            const parsed = JSON.parse(await file.text());
            const payload = {
                tm_values: parsed.tm_values || [],
                pesdb_records: parsed.pesdb_records || [],
                request_ids: parsed.request_ids || [],
            };
            const { data } = await api.post("/admin/update-requests/apply", payload);
            setApplyResult(data);
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message || "File non valido");
        } finally { setBusy(""); }
    };

    // Rileva giocatori corrotti dal bug "Offensive Awareness"/"Piede" → li accoda come richieste
    const scanCorrupted = async () => {
        setErr(""); setScanResult(null); setBusy("scan");
        try {
            const { data } = await api.post("/admin/update-requests/scan-pesdb-issues");
            setScanResult(data);
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(""); }
    };

    const pending = data?.pending || [];

    return (
        <div data-testid="admin-update-requests-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-9d7cff))" }}>// PONTE AGGIORNAMENTI</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Richieste aggiornamento TM/PESDB</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-5 max-w-3xl leading-relaxed">
                I presidenti richiedono l'aggiornamento dei propri giocatori dai pulsanti
                <span className="text-t9d7cff"> "Richiedi agg. TM/PESDB"</span> nella rosa. In produzione lo
                scraping è bloccato, quindi qui arrivano le richieste in attesa. Flusso:
                <strong className="text-tffffff"> ① in PRODUZIONE esporta</strong> →
                <strong className="text-tffffff"> ② in PREVIEW elabora</strong> (scraping) →
                <strong className="text-tffffff"> ③ in PRODUZIONE applica</strong> il file dei risultati.
            </p>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-2 mb-4" data-testid="ur-err">{err}</div>
            )}

            {/* Rilevamento automatico giocatori corrotti dal bug "Offensive Awareness"/"Piede" */}
            <div className="op-card p-4 mb-4 border-tffb020/30">
                <div className="op-label text-[10px] text-tffb020 mb-1">RILEVAMENTO BUG</div>
                <div className="text-tffffff text-sm font-medium mb-2">Rileva giocatori con dati PESDB corrotti</div>
                <p className="op-mono text-[10px] text-t5c6370 mb-3 leading-relaxed max-w-2xl">
                    PESDB ha rinominato "Offensive Awareness" → "Attacking Awareness": chi è stato
                    ri-agganciato/risincronizzato prima del fix può aver perso "Comportamento offensivo" o avere il
                    "Piede" non tradotto. Premi per accodare automaticamente questi giocatori come richieste PESDB (passo ①).
                </p>
                <button
                    onClick={scanCorrupted}
                    disabled={busy === "scan"}
                    className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-tffb020 text-tffb020 hover:bg-tffb020/10 transition-colors disabled:opacity-40"
                    data-testid="ur-scan-btn"
                >
                    {busy === "scan" ? "Scansione…" : "🔍 Rileva giocatori corrotti"}
                </button>
                {scanResult && (
                    <div className="op-mono text-[10px] text-t7ce38b mt-2" data-testid="ur-scan-result">
                        ✓ Nuove richieste create: {scanResult.created} · già in coda: {scanResult.already_pending}
                        {scanResult.skipped_no_roster > 0 ? ` · senza rosa (ignorati): ${scanResult.skipped_no_roster}` : ""}
                    </div>
                )}
            </div>

            {/* I 3 passi del ponte */}
            <div className="grid md:grid-cols-3 gap-3 mb-6">
                {/* ① Esporta (PRODUZIONE) */}
                <div className="op-card p-4">
                    <div className="op-label text-[10px] text-t2bb6ff mb-1">① PRODUZIONE</div>
                    <div className="text-tffffff text-sm font-medium mb-2">Esporta richieste</div>
                    <p className="op-mono text-[10px] text-t5c6370 mb-3 leading-relaxed">
                        Scarica il file con le {pending.length} richieste in attesa, da elaborare in preview.
                    </p>
                    <button
                        onClick={exportRequests}
                        disabled={busy === "export" || pending.length === 0}
                        className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff/10 transition-colors disabled:opacity-40 w-full"
                        data-testid="ur-export-btn"
                    >
                        {busy === "export" ? "..." : "⬇ Esporta richieste"}
                    </button>
                    {pending.length === 0 && (
                        <div className="op-mono text-[10px] text-t5c6370 mt-2" data-testid="ur-export-empty-hint">
                            Nessuna richiesta in attesa.
                        </div>
                    )}
                </div>

                {/* ② Elabora (PREVIEW) */}
                <div className="op-card p-4">
                    <div className="op-label text-[10px] text-tffb020 mb-1">② PREVIEW</div>
                    <div className="text-tffffff text-sm font-medium mb-2">Elabora (scraping)</div>
                    <p className="op-mono text-[10px] text-t5c6370 mb-3 leading-relaxed">
                        Carica il file delle richieste: la preview scarica i dati TM/PESDB e genera il file dei risultati.
                    </p>
                    <input ref={processInputRef} type="file" accept="application/json,.json" className="hidden" onChange={processFile} data-testid="ur-process-input" />
                    <button
                        onClick={() => processInputRef.current?.click()}
                        disabled={busy === "process"}
                        className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-tffb020 text-tffb020 hover:bg-tffb020/10 transition-colors disabled:opacity-40 w-full"
                        data-testid="ur-process-btn"
                    >
                        {busy === "process" ? "Elaborazione in corso…" : "⚙ Carica & elabora"}
                    </button>
                    {processProgress && (
                        <div className="op-mono text-[10px] text-tffb020 mt-2" data-testid="ur-process-progress">
                            {processProgress.phase === "pesdb" ? "Scraping PESDB…" : "Scraping TM…"}
                            {" "}{processProgress.done}/{processProgress.total}
                            <span className="text-t5c6370"> · non chiudere la pagina</span>
                        </div>
                    )}
                    {processResult && (
                        <div className="op-mono text-[10px] text-t7ce38b mt-2" data-testid="ur-process-result">
                            ✓ TM: {processResult.summary?.tm ?? 0} · PESDB: {processResult.summary?.pesdb ?? 0} (file scaricato)
                        </div>
                    )}
                </div>

                {/* ③ Applica (PRODUZIONE) */}
                <div className="op-card p-4">
                    <div className="op-label text-[10px] text-t7ce38b mb-1">③ PRODUZIONE</div>
                    <div className="text-tffffff text-sm font-medium mb-2">Applica risultati</div>
                    <p className="op-mono text-[10px] text-t5c6370 mb-3 leading-relaxed">
                        Carica il file dei risultati: aggiorna i giocatori e segna le richieste come completate.
                    </p>
                    <input ref={applyInputRef} type="file" accept="application/json,.json" className="hidden" onChange={applyFile} data-testid="ur-apply-input" />
                    <button
                        onClick={() => applyInputRef.current?.click()}
                        disabled={busy === "apply"}
                        className="op-mono text-xs uppercase tracking-wider px-4 py-2 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b/10 transition-colors disabled:opacity-40 w-full"
                        data-testid="ur-apply-btn"
                    >
                        {busy === "apply" ? "..." : "✅ Carica & applica"}
                    </button>
                    {applyResult && (
                        <div className="op-mono text-[10px] text-t7ce38b mt-2" data-testid="ur-apply-result">
                            ✓ TM {applyResult.tm_applied} · PESDB agg. {applyResult.pesdb_updated}/creati {applyResult.pesdb_created} · completate {applyResult.requests_marked_done}
                        </div>
                    )}
                </div>
            </div>

            {/* Elenco richieste pending */}
            <div className="op-card overflow-x-auto">
                <div className="px-3 py-2 op-label text-[10px] text-t9d7cff border-b border-t22252a">
                    // RICHIESTE IN ATTESA · {pending.length}
                </div>
                <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                        <tr>
                            <th className="py-2 px-3 text-left">Giocatore</th>
                            <th className="py-2 px-3 text-left">Tipo</th>
                            <th className="py-2 px-3 text-left">Squadra</th>
                            <th className="py-2 px-3 text-left">Richiesta da</th>
                            <th className="py-2 px-3 text-right">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pending.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="py-6 px-3 text-center op-mono text-xs text-t5c6370">
                                    Nessuna richiesta in attesa.
                                </td>
                            </tr>
                        ) : (
                            pending.map((r) => (
                                <tr key={r.id} className="border-b border-t1a1d24 hover:bg-t0f1115" data-testid={`ur-row-${r.id}`}>
                                    <td className="py-2 px-3 text-tffffff">
                                        {r.pes_id != null && r.pes_id !== "" ? (
                                            <Link
                                                to={`/giocatori/${r.pes_id}`}
                                                className="text-tffffff hover:text-t2bb6ff hover:underline transition-colors"
                                                data-testid={`ur-player-link-${r.id}`}
                                            >
                                                {r.player_name || "—"}
                                            </Link>
                                        ) : (
                                            r.player_name || "—"
                                        )}
                                    </td>
                                    <td className="py-2 px-3">
                                        <span className={`op-mono text-[10px] uppercase px-1.5 py-0.5 border ${r.kind === "tm" ? "border-t00bfff/40 text-t00bfff" : "border-t9d7cff/40 text-t9d7cff"}`}>
                                            {r.kind === "tm" ? "TM" : "PESDB"}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 op-mono text-tc9ccd3">{r.team_name || "—"}</td>
                                    <td className="py-2 px-3 op-mono text-t8f95a3">{r.requested_by_name || "—"}</td>
                                    <td className="py-2 px-3 text-right">
                                        <button
                                            onClick={() => deleteRequest(r)}
                                            disabled={busy === `del-${r.id}`}
                                            className="op-mono text-[10px] uppercase px-2 py-1 border border-tff3b30/40 text-tff3b30 hover:bg-tff3b30 hover:text-black disabled:opacity-50 transition-colors"
                                            title="Elimina questa richiesta (già sistemata a mano)"
                                            data-testid={`ur-delete-${r.id}`}
                                        >
                                            {busy === `del-${r.id}` ? "…" : "✕ Elimina"}
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
